import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import type { Token } from "@stripe/stripe-js";
import { Divider, Spin, message } from "@dreambigger/design-system";
import { eventApi } from "@dreambigger/shared/src/api/weserve/api-helper";
import { CreateExternalTransactionParams } from "@dreambigger/shared/src/api/events";
import { Community, Event as EventType } from "@dreambigger/shared/src/types";
import { browserTimeZone } from "@dreambigger/shared/src/utils";
import {
  DonationFormValues,
  StripeCardValidationState,
} from "../../types/forms";

const CARD_STYLE_BASE = {
  color: "#262626",
  fontFamily: "sans-serif",
  fontSmoothing: "antialiased",
  fontSize: "12.5pt",
  "::placeholder": {
    color: "#7d7d7d",
    display: "flex",
    alignItems: "center",
  },
};

const CARD_STYLE_INVALID = {
  color: "#fa755a",
  iconColor: "#fa755a",
};

const CARD_NUMBER_OPTIONS = {
  placeholder: "XXXX XXXX XXXX XXXX",
  style: { base: CARD_STYLE_BASE, invalid: CARD_STYLE_INVALID },
  disableLink: true,
};

const BASIC_CARD_OPTIONS = {
  style: { base: CARD_STYLE_BASE, invalid: CARD_STYLE_INVALID },
};

export interface StripeCardFormProps {
  formData: DonationFormValues;
  event: EventType;
  community: Community;
  setPaymentComplete: (value: boolean) => void;
}

// Initial validation state
const INITIAL_VALIDATION_STATE: StripeCardValidationState = {
  cardNumber: false,
  expiry: false,
  cvc: false,
  name: false,
  zip: false,
  terms: false,
};

export default function StripeCardForm({
  formData,
  event,
  community,
  setPaymentComplete,
}: StripeCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  const initialFullName = `${formData.firstName} ${formData.lastName}`.trim();

  // Consolidated validation state (fixed: was 8 separate useState calls)
  const [validation, setValidation] = useState<StripeCardValidationState>({
    ...INITIAL_VALIDATION_STATE,
    name: initialFullName.length > 0,
  });
  const [fullName, setFullName] = useState(initialFullName);
  const [zipCode, setZipCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Ref to capture formData at submission time to prevent race conditions
  const submittedFormDataRef = useRef<DonationFormValues | null>(null);
  // Ref to prevent double submission (React Strict Mode runs effects twice)
  const transactionSubmittedRef = useRef<boolean>(false);

  // Check if terms checkbox is required
  const termsRequired = !!(
    community?.organization?.meta?.externalCampaign?.termsOfUseRequire &&
    community?.organization?.meta?.externalCampaign?.termsOfUseOrganization &&
    community?.organization?.meta?.externalCampaign?.termsOfUseURL
  );

  // Compute form validity from consolidated state
  const isFormValid = useMemo(() => {
    // Check all required fields except terms (which is conditionally required)
    const { terms, ...requiredFields } = validation;
    const baseFieldsValid = Object.values(requiredFields).every(Boolean);

    // If terms is required, check it; otherwise ignore it
    if (termsRequired) {
      return baseFieldsValid && terms;
    }
    return baseFieldsValid;
  }, [validation, termsRequired]);

  // Update validation state helper
  const updateValidation = useCallback(
    (field: keyof StripeCardValidationState, value: boolean) => {
      setValidation((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  useEffect(() => {
    if (!elements) return;
    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);

    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) return;

    const cardNumChange = (event: {
      complete: boolean;
      error?: unknown;
      empty: boolean;
    }) => {
      updateValidation(
        "cardNumber",
        event.complete && !event.error && !event.empty
      );
    };
    const cardExpiryChange = (event: {
      complete: boolean;
      error?: unknown;
      empty: boolean;
    }) => {
      updateValidation(
        "expiry",
        event.complete && !event.error && !event.empty
      );
    };
    const cardCvcChange = (event: {
      complete: boolean;
      error?: unknown;
      empty: boolean;
    }) => {
      updateValidation("cvc", event.complete && !event.error && !event.empty);
    };

    cardNumberElement.on("change", cardNumChange);
    cardExpiryElement.on("change", cardExpiryChange);
    cardCvcElement.on("change", cardCvcChange);

    // Clean up event listeners on unmount or when elements change
    return () => {
      cardNumberElement.off("change", cardNumChange);
      cardExpiryElement.off("change", cardExpiryChange);
      cardCvcElement.off("change", cardCvcChange);
    };
  }, [elements, updateValidation]);

  const [token, setToken] = useState<Token | undefined>(undefined);

  // When token is received from stripe, make api call to POST /external-transactions
  useEffect(() => {
    if (!token) return;

    // Prevent double submission (React Strict Mode runs effects twice in development)
    if (transactionSubmittedRef.current) return;
    transactionSubmittedRef.current = true;

    // Use the captured form data from submission time to prevent race conditions
    const capturedFormData = submittedFormDataRef.current;
    if (!capturedFormData) return;

    const saveTransaction = async () => {
      // guest.zip uses the step 1 zipCode if provided, otherwise falls back to billing zip
      // The billing zipCode is used for the Stripe token (address_zip)
      const guestZip = capturedFormData.zipCode || zipCode;

      const externalTransactionPayload: CreateExternalTransactionParams = {
        guest: {
          firstName: capturedFormData.firstName,
          lastName: capturedFormData.lastName,
          email: capturedFormData.email,
          zip: guestZip,
          company: capturedFormData.companyName || "",
          donorAddress: null,
          timeZone: browserTimeZone(),
        },
        transactions: [
          {
            isAmountPublic: !capturedFormData.hideAmount,
            isPublic: !capturedFormData.hideName,
            product: {
              id: event?.campaign?.id,
              price: capturedFormData.amount.toString() + ".00",
            },
            description: event.name,
            type: "donation",
            quantity: 1,
          },
        ],
        comments: capturedFormData.donationNote || "-",
        stripe: token.id,
        flags: {
          fundraiserNotifications: capturedFormData.optIn1 || false,
          relatedEventNotifications: capturedFormData.optIn2 || false,
          event_id: event.id,
        },
      };

      try {
        await eventApi.createExternalTransaction(externalTransactionPayload);
        setPaymentComplete(true);
        setLoading(false);
      } catch (error: unknown) {
        const axiosError = error as {
          response?: { data?: unknown; status?: number; headers?: unknown };
          request?: unknown;
        };
        const errorDetails = axiosError?.response?.data as
          | { error?: string; message?: string; details?: unknown }
          | undefined;
        const errorMessage =
          errorDetails?.error ||
          errorDetails?.message ||
          "Failed to save donation. Please try again.";
        message.error(errorMessage);
        setLoading(false);
      }
    };

    saveTransaction();
  }, [token, event, setPaymentComplete, zipCode]);

  const handleSubmit = async (
    submitEvent: React.FormEvent<HTMLFormElement>
  ) => {
    submitEvent.preventDefault();
    setLoading(true);

    if (!isFormValid) {
      setLoading(false);
      return;
    }

    // Capture form data at submission time to prevent race conditions
    submittedFormDataRef.current = { ...formData };

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    const cardNumberElement = elements.getElement("cardNumber");
    const cardExpiryElement = elements.getElement("cardExpiry");
    const cardCvcElement = elements.getElement("cardCvc");

    const options = {
      address_zip: zipCode,
      name: fullName,
    };

    if (cardNumberElement && cardExpiryElement && cardCvcElement) {
      const result = await stripe.createToken(cardNumberElement, options);
      if (result.error) {
        message.error(result.error.message);
        setLoading(false); // Reset loading state on error
      } else {
        // Token created successfully - loading will be reset after transaction completes
        setToken(result.token);
      }
    } else {
      // Elements not available
      setLoading(false);
    }
  };

  const handleFullNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFullName(value);
      updateValidation("name", value.length > 0);
    },
    [updateValidation]
  );

  const handleZipCodeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setZipCode(value);
      updateValidation("zip", value.length === 5);
    },
    [updateValidation]
  );

  const handleTermsCheckChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateValidation("terms", event.target.checked);
    },
    [updateValidation]
  );

  return (
    <div style={{ position: "relative" }}>
      <form id="stripe-card-form" onSubmit={handleSubmit}>
        <div className="fwb f-4 pb-2">Name as it appears on credit card</div>
        <input
          className="w-100 h-7 b-gray-6 ba mb-5 pl-3"
          style={{ outline: "none", fontSize: "12.5pt" }}
          placeholder="Full Name"
          value={fullName}
          onChange={handleFullNameChange}
        />
        <div className="f-4 fwb pb-2">Credit card number</div>
        <div className="flex items-center mb-5 flex-wrap">
          <CardNumberElement
            className="w-100 md_w-auto md_flex-grow-1 h-7 b-gray-6 ba pt-2 pl-3"
            options={CARD_NUMBER_OPTIONS}
          />
          <img
            className="mt-2 md_mt-0 md_ml-2"
            style={{ height: "24px" }}
            src="https://res.cloudinary.com/cotribute/image/upload/v1533594082/Visa_Mastercard_Discover_and_AMEX_logo4x_soyan5.png"
            alt="Visa, Mastercard, Discover, AMEX"
          />
        </div>

        <div className="flex flex-wrap">
          <div className="flex flex-column w-100 md_w-40 md_pr-4">
            <div className="f-4 fwb pb-2">Expiration Date</div>
            <CardExpiryElement
              className="w-100 h-7 b-gray-6 ba pt-2 pl-3 mb-5"
              options={BASIC_CARD_OPTIONS}
            />
          </div>
          <div className="flex flex-column w-30 md_w-20 pr-3 md_pr-4">
            <div className="f-4 fwb pb-2">CVV</div>
            <CardCvcElement
              className="w-100 h-7 b-gray-6 ba pt-2 pl-3 mb-5"
              options={BASIC_CARD_OPTIONS}
            />
          </div>
          <div className="flex flex-column w-70 md_w-40">
            <div className="f-4 fwb pb-2">Postal/Zip Code</div>
            <input
              className="w-100 h-7 b-gray-6 ba mb-5 pl-3"
              style={{ outline: "none", fontSize: "12.5pt" }}
              placeholder="Zip Code"
              maxLength={5}
              value={zipCode}
              onChange={handleZipCodeChange}
            />
          </div>
        </div>
        {/* SSL Security Message */}
        <p className="f-3 gray-7 mb-3">
          Your credit card transaction is protected by SSL.
        </p>

        {/* SSL Site Seal */}
        <div className="mb-4">
          <a
            href="https://seal.starfieldtech.com/verifySeal?sealID=Ek8UmjZ1NcF90Z3rKhMD5xrIcA6gccXG26KRO84YOccNqyjujBw1CWMED"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://res.cloudinary.com/cotribute/image/upload/v1420580290/site_seal_ehr5tx.gif"
              alt="SSL Site Seal"
            />
          </a>
        </div>

        {/* Terms of Use Checkbox - only shown if termsOfUseRequire is set */}
        {community?.organization?.meta?.externalCampaign?.termsOfUseRequire &&
          community?.organization?.meta?.externalCampaign
            ?.termsOfUseOrganization &&
          community?.organization?.meta?.externalCampaign?.termsOfUseURL && (
            <label className="db mb-4">
              <input
                className="mr-2"
                type="checkbox"
                checked={validation.terms}
                onChange={handleTermsCheckChange}
              />
              By donating, I accept{" "}
              {
                community?.organization?.meta?.externalCampaign
                  ?.termsOfUseOrganization
              }{" "}
              <a
                className="underline primary"
                href={
                  community?.organization?.meta?.externalCampaign?.termsOfUseURL
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
              .
            </label>
          )}
        <Divider className="mt-4" />
        <div className="flex justify-center mt-4">
          <button
            className={`fwb ttu f-3 ${
              isFormValid
                ? "ant-btn ant-btn-primary darkenPrimaryBkd bg-primary b-primary white"
                : ""
            }`}
            style={{
              borderRadius: "9999px",
              width: "260px",
              height: "48px",
              border: "none",
              ...(!isFormValid
                ? {
                    backgroundColor: "#d9d9d9",
                    color: "rgba(0, 0, 0, 0.25)",
                  }
                : {}),
              cursor: isFormValid ? "pointer" : "not-allowed",
            }}
            disabled={!isFormValid}
          >
            SUBMIT DONATION
          </button>
        </div>
        {/* Loading overlay */}
        {loading && (
          <div
            className="w-50 h-75"
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <Spin />
            <div className="f-4 gray-8 lh-4 mb-6">
              Processing your donation...
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
